import { FiBarChart2 } from 'react-icons/fi';

export const task1DataComparisonData = {
  courseId: 'task1-data-comparison',
  courseType: 'writing-course',
  title: 'Task 1 Data Comparison',
  icon: FiBarChart2,
  overview: {
    impact: 'Compare and contrast data effectively in Task 1',
    focus: 'Comparison structures, selecting key features, grouping data',
    uses: 'Writing Task 1 (bar charts, tables, pie charts, multiple graphs)',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'Comparison Language',
      content: `<p>Comparing data is the central skill in Writing Task 1. You need a range of structures to compare figures <strong>within a single category</strong> and <strong>across different categories</strong>.</p>`,
      table: {
        headers: ['Structure', 'Usage', 'Example'],
        rows: [
          ['while / whereas', 'Contrast two data points in one sentence', 'Country A spent 30% on education, while Country B spent only 15%.'],
          ['compared to / in comparison with', 'Explicit comparison', 'Exports were low compared to the previous year.'],
          ['more/less … than', 'Direct comparative', 'Women earned significantly more than men in this sector.'],
          ['twice / three times as … as', 'Multiplier comparison', 'Spending in 2020 was nearly twice as high as in 2010.'],
          ['the highest / the lowest', 'Superlative ranking', 'Japan had the highest life expectancy of all countries surveyed.'],
          ['similar to / almost the same as', 'Showing similarity', 'Figures for France were similar to those for Italy.'],
        ],
      },
      points: [
        '"While" and "Whereas" are the most useful comparison connectors — they let you contrast two points in a single sentence',
        'Use <strong>comparatives</strong> (more, less, higher, lower) for two items and <strong>superlatives</strong> (most, least, highest, lowest) for three or more',
        'Include approximation language when data is not exact: "approximately", "roughly", "just under", "just over"',
      ],
      examples: [
        {
          label: 'While / Whereas',
          text: 'In 2015, the USA allocated 16% of its budget to healthcare, whereas the UK dedicated approximately 9%.',
        },
        {
          label: 'Multiplier',
          text: 'The number of tourists visiting Spain was nearly three times as high as the figure for Portugal.',
        },
      ],
    },
    {
      title: 'Selecting Key Features',
      content: `<p>You cannot describe every number in the data. IELTS examiners reward candidates who <strong>select and report the main features</strong> rather than listing every detail.</p>
      <p>Ask yourself these questions when analysing the data:</p>`,
      points: [
        '<strong>What is the highest and lowest?</strong> — Identify the extremes',
        '<strong>What is the overall trend?</strong> — Is the general direction up, down, or stable?',
        '<strong>Are there any significant differences?</strong> — Large gaps between categories deserve mention',
        '<strong>Are there any surprising similarities?</strong> — Categories that are unexpectedly close in value',
        '<strong>Are there any turning points?</strong> — Moments where a trend reverses',
      ],
      examples: [
        {
          label: 'Key feature selection from a bar chart',
          text: 'Rather than listing all eight countries\' spending, focus on: "The USA and Switzerland had by far the highest healthcare spending, while India and Nigeria had the lowest. Most other countries clustered in the middle range between 5% and 10% of GDP."',
        },
      ],
      warning: 'Do not try to mention every data point. A response that describes five key features well will score higher than one that lists ten features superficially.',
    },
    {
      title: 'Grouping Data',
      content: `<p>Effective Task 1 responses <strong>group similar data together</strong> rather than describing items one by one. Grouping shows the examiner that you can analyse and organise information.</p>
      <p>Common grouping strategies:</p>`,
      points: [
        '<strong>By trend direction</strong> — Group items that increase together, then those that decrease',
        '<strong>By magnitude</strong> — Group the highest values, then the middle range, then the lowest',
        '<strong>By category</strong> — Group countries, age groups, or genders that behave similarly',
        '<strong>By time period</strong> — Describe what happened in the first half, then the second half',
      ],
      examples: [
        {
          label: 'Grouped by magnitude',
          text: 'The USA and Germany had the highest car ownership rates, at over 600 vehicles per 1,000 people. By contrast, China and India had considerably lower rates, both below 100 per 1,000. The remaining countries — Brazil, Russia, and South Africa — fell in the mid-range, between 150 and 300 per 1,000.',
        },
        {
          label: 'Grouped by trend',
          text: 'Both online sales and mobile transactions showed upward trends over the decade. In contrast, in-store purchases and catalogue orders declined steadily throughout the same period.',
        },
      ],
    },
    {
      title: 'Comparing Different Chart Types',
      content: `<p>The comparison language you use depends partly on the type of visual data you receive.</p>`,
      table: {
        headers: ['Chart Type', 'What to Compare', 'Useful Language'],
        rows: [
          ['Bar chart', 'Heights of bars across categories/years', '"X was higher/lower than Y", "The tallest bar…"'],
          ['Line graph', 'Trends over time across multiple lines', '"X rose while Y fell", "Both lines show…"'],
          ['Pie chart', 'Proportions within a whole', '"The largest proportion…", "X accounted for…"'],
          ['Table', 'Exact figures across rows and columns', '"The figure for X was almost double that of Y"'],
          ['Two charts combined', 'Relationships between the two data sets', '"In contrast to the first chart…", "This corresponds with…"'],
        ],
      },
      points: [
        'For <strong>pie charts</strong>, use proportion language: "accounted for", "made up", "represented", "comprised"',
        'For <strong>tables</strong>, you can cite exact figures more freely than in graphs — but still select key comparisons',
        'When given <strong>two charts</strong>, look for connections between them and mention these in your overview',
        'Use "respectively" to compare two figures efficiently: "The USA and UK spent 16% and 9%, respectively"',
      ],
      examples: [
        {
          label: 'Pie chart comparison',
          text: 'Food accounted for the largest share of household expenditure at 35%, while entertainment represented the smallest proportion at just 5%.',
        },
        {
          label: 'Table comparison',
          text: 'The figure for Australia (78 years) was almost identical to that of Canada (79 years), whereas life expectancy in Sierra Leone was significantly lower at 52 years.',
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Choose the Correct Comparison Phrase',
      type: 'select',
      instructions: 'Select the most appropriate phrase to complete each comparison sentence.',
      questions: [
        {
          text: 'The population of China was approximately ___ that of the USA in 2020.',
          options: [
            'four times as large as',
            'slightly less than',
            'similar to',
            'lower than',
          ],
          answer: 'four times as large as',
          explanation: 'China\'s population (~1.4 billion) is roughly four times the USA\'s (~330 million). A multiplier comparison is the most precise choice.',
        },
        {
          text: 'Spending on education increased by 20%, ___ spending on defence fell by 5%.',
          options: [
            'whereas',
            'furthermore',
            'for example',
            'as a result',
          ],
          answer: 'whereas',
          explanation: '"Whereas" contrasts two opposing trends in one sentence. The other options signal addition, example, or cause/effect.',
        },
        {
          text: 'Japan had ___ life expectancy of all the countries shown.',
          options: [
            'the highest',
            'a higher',
            'the most high',
            'more higher',
          ],
          answer: 'the highest',
          explanation: 'When comparing more than two items, use the superlative "the highest". "A higher" is comparative (two items). "The most high" and "more higher" are grammatically incorrect.',
        },
        {
          text: 'Rainfall in London was ___ that in Madrid during summer.',
          options: [
            'slightly higher than',
            'while',
            'the highest',
            'similar',
          ],
          answer: 'slightly higher than',
          explanation: 'Comparing two specific items uses the comparative form "higher than". "While" needs a full clause; "the highest" is superlative; "similar" would need "to".',
        },
      ],
    },
    {
      title: 'Complete Comparison Sentences',
      type: 'fill-in',
      instructions: 'Fill in the blank with an appropriate comparison word or phrase.',
      questions: [
        {
          text: 'The number of cars in the USA was significantly ___ than in Japan.',
          answer: 'higher',
          explanation: 'Comparing two countries requires the comparative "higher than".',
        },
        {
          text: 'Germany spent 10% of GDP on healthcare, ___ France spent 11%.',
          answer: 'while',
          explanation: '"While" (or "whereas") contrasts two data points from different categories in one sentence.',
        },
        {
          text: 'Food ___ the largest proportion of household spending at 40%.',
          answer: 'accounted for',
          explanation: '"Accounted for" is standard language for describing proportions, especially in pie charts.',
        },
        {
          text: 'Exports in 2020 were ___ twice as high as in 2010.',
          answer: 'almost',
          explanation: 'Approximation words like "almost", "nearly", and "roughly" show you are interpreting the data rather than guessing exact figures.',
        },
      ],
    },
    {
      title: 'Identify Key Features',
      type: 'select',
      instructions: 'Which features should you prioritise in your Task 1 response?',
      questions: [
        {
          text: 'Bar chart showing spending by 6 countries: USA ($8,000), Germany ($5,000), UK ($4,500), Japan ($4,200), Brazil ($900), India ($200). What is the MOST important feature to mention?',
          options: [
            'The enormous gap between the highest spender (USA) and the lowest (India)',
            'The exact figure for each individual country',
            'That the UK spent $4,500',
            'That Japan spent $4,200',
          ],
          answer: 'The enormous gap between the highest spender (USA) and the lowest (India)',
          explanation: 'The biggest contrast is the most significant feature. Individual mid-range figures are less important than the overall pattern.',
        },
        {
          text: 'Line graph: Line A rises steeply. Line B rises slowly. Line C stays flat. Line D drops. Which comparison is LEAST important?',
          options: [
            'The exact crossing point of lines B and C',
            'The contrast between rising Line A and falling Line D',
            'That Lines A and B both show upward trends but at different rates',
            'That Line C remained stable throughout',
          ],
          answer: 'The exact crossing point of lines B and C',
          explanation: 'Main trends and contrasts matter more than pinpointing where two mid-range lines intersect. Focus on the big picture.',
        },
        {
          text: 'Pie chart: Housing 35%, Food 25%, Transport 20%, Entertainment 10%, Other 10%. What grouping works best?',
          options: [
            'Group the two largest categories (Housing + Food = 60%), then contrast with the smaller ones',
            'Describe each slice in clockwise order',
            'Only mention Housing because it is the biggest',
            'Compare Entertainment and Other because they are the same',
          ],
          answer: 'Group the two largest categories (Housing + Food = 60%), then contrast with the smaller ones',
          explanation: 'Grouping by magnitude (large vs small) shows analytical ability and keeps the description organised.',
        },
      ],
    },
    {
      title: 'Write Comparison Sentences',
      type: 'writing',
      instructions: 'Write comparison sentences using the data provided.',
      questions: [
        {
          text: 'Data: In 2020, Country A exported $50 billion, Country B exported $48 billion, Country C exported $12 billion. Compare these figures.',
          example: 'Countries A and B had almost identical export values at approximately $50 billion, whereas Country C\'s exports were significantly lower at just $12 billion.',
          tip: 'Group similar data together (A and B) and contrast with the outlier (C). Use "whereas" or "while".',
        },
        {
          text: 'Data: In 2000, 10% of the population used the internet. In 2020, 85% used the internet. Describe this change with a comparison.',
          example: 'Internet usage in 2020 was more than eight times higher than in 2000, rising from just 10% to 85% of the population.',
          tip: 'Use a multiplier comparison ("X times as high/large as") when the change is dramatic.',
        },
        {
          text: 'Data: Pie chart shows Food 40%, Rent 30%, Transport 15%, Clothing 10%, Other 5%. Describe the largest and smallest.',
          example: 'Food represented the largest share of expenditure at 40%, which was eight times the proportion spent on Other items, the smallest category at only 5%.',
          tip: 'Use proportion language ("accounted for", "represented") and contrast the extremes.',
        },
      ],
    },
    {
      title: 'Order a Data Comparison Paragraph',
      type: 'select',
      instructions: 'Put the sentences in the best order for a clear data comparison paragraph.',
      questions: [
        {
          text: 'Which sentence should come FIRST in a paragraph comparing healthcare spending across countries?',
          options: [
            'The USA had the highest healthcare expenditure at $10,000 per capita.',
            'For instance, Germany spent $5,500 per capita.',
            'In contrast, India had the lowest spending at just $200.',
            'Overall, there were significant differences in healthcare spending between developed and developing nations.',
          ],
          answer: 'The USA had the highest healthcare expenditure at $10,000 per capita.',
          explanation: 'Start with the most significant data point (the highest value) to anchor the paragraph before adding comparisons.',
        },
        {
          text: 'Which sentence should come LAST?',
          options: [
            'Clearly, developed nations allocated far more resources to healthcare than their developing counterparts.',
            'The USA had the highest healthcare expenditure.',
            'Germany and the UK occupied the middle range.',
            'India spent only $200 per person.',
          ],
          answer: 'Clearly, developed nations allocated far more resources to healthcare than their developing counterparts.',
          explanation: 'A concluding sentence that summarises the overall pattern should end the paragraph.',
        },
        {
          text: 'After mentioning the USA\'s high spending, which sentence logically comes next?',
          options: [
            'In contrast, India had the lowest spending at just $200 per capita.',
            'The weather in the USA is varied.',
            'Overall, spending increased.',
            'Clearly, all countries spend on healthcare.',
          ],
          answer: 'In contrast, India had the lowest spending at just $200 per capita.',
          explanation: 'After presenting the highest value, contrasting it immediately with the lowest creates a clear comparison structure.',
        },
        {
          text: 'Where should "Germany and the UK occupied the middle range, spending $5,500 and $4,800 respectively" go?',
          options: [
            'After the highest and lowest are established, before the concluding sentence',
            'As the very first sentence',
            'As the very last sentence',
            'It should be removed — it is unnecessary',
          ],
          answer: 'After the highest and lowest are established, before the concluding sentence',
          explanation: 'The logical order is: highest → lowest → middle range → concluding summary. This groups data by magnitude.',
        },
      ],
    },
  ],
  tips: [
    'Always use "while" or "whereas" to contrast two data points in one sentence — this shows sophisticated comparison skills',
    'Group data before writing: high values together, low values together, similar trends together',
    'Use multiplier language ("twice as high", "three times more") for large differences — it is more impactful than just stating numbers',
    'Do not describe every data point — select the 4-5 most significant features and compare them',
  ],
  dailyPractice: [
    'Find a chart online and write 5 comparison sentences about it using different structures',
    'Practise grouping: take any data set and sort items into 2-3 groups before writing',
    'Write a full Task 1 response in 20 minutes, focusing on comparisons rather than listing individual values',
    'Read Task 1 model answers and underline all comparison language — build your own list of phrases',
  ],
  backPath: '/exams/english/#writing',
  backLabel: 'Back to Writing',
  aiContext: 'Task 1 Data Comparison: Comparing and contrasting data using comparison structures (while, whereas, compared to, multipliers, superlatives), selecting key features, grouping data, and applying these skills to different chart types in IELTS Writing Task 1.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...task1DataComparisonData,
      title: task1DataComparisonData.title + ' (Foundations)',
      overview: { ...task1DataComparisonData.overview, duration: '15-20 minutes' },
      sections: task1DataComparisonData.sections.slice(0, Math.max(2, Math.ceil(task1DataComparisonData.sections.length / 2))),
      exercises: task1DataComparisonData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...task1DataComparisonData,
      title: task1DataComparisonData.title + ' (Advanced)',
      overview: { ...task1DataComparisonData.overview, duration: '30-35 minutes' },
      sections: task1DataComparisonData.sections,
      exercises: task1DataComparisonData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(task1DataComparisonData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return task1DataComparisonData;
}
